import axios from 'axios';
import { auth } from './firebase';

// URL prefixes that should receive a Firebase ID token. Add new protected
// backends here — both the axios interceptor and authFetch consult this list.
const AUTH_PREFIXES: string[] = [
  process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT,
].filter((p): p is string => typeof p === 'string' && p.length > 0);

const shouldAuthenticate = (url: string | undefined): boolean => {
  if (!url) return false;
  return AUTH_PREFIXES.some((prefix) => url.startsWith(prefix));
};

const getBearerToken = async (): Promise<string | null> => {
  const token = await auth.currentUser?.getIdToken();
  return token ?? null;
};

// Wraps native fetch with automatic Bearer-token injection for whitelisted URLs.
// Skips injection if the caller already set Authorization (so manual overrides
// keep working).
export const authFetch = async (
  input: RequestInfo,
  init: RequestInit = {}
): Promise<Response> => {
  const url = typeof input === 'string' ? input : input.url;
  if (!shouldAuthenticate(url)) {
    return fetch(input, init);
  }

  const headers = new Headers(init.headers || {});
  if (!headers.has('Authorization')) {
    const token = await getBearerToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  return fetch(input, { ...init, headers });
};

// Registers a global axios request interceptor. Idempotent — only attaches once.
let axiosInterceptorRegistered = false;
const registerAxiosInterceptor = (): void => {
  if (axiosInterceptorRegistered) return;
  axios.interceptors.request.use(async (config) => {
    const url = config.url ?? '';
    const fullUrl = config.baseURL ? `${config.baseURL}${url}` : url;
    if (!shouldAuthenticate(fullUrl)) return config;

    const headers: any = config.headers ?? {};
    if (!headers.Authorization && !headers.authorization) {
      const token = await getBearerToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    config.headers = headers;
    return config;
  });
  axiosInterceptorRegistered = true;
};

registerAxiosInterceptor();
